import { useEffect, useState } from "react";
import { useAdminViewState } from "../../lib/admin/adminViewState";
import { apiFetch } from "../../api/client";
import { formatBytes, formatNumber } from "../../lib/admin/adminFormat";

export type AdminSummary = {
  kind: "school" | "external";
  users: { total: number; new_7d: number };
  boards: {
    active: number;
    trashed: number;
    touched_24h: number;
    touched_7d: number;
  };
  /** Доски без аккаунта — общие для обеих админок. */
  guest: { boards: number; v_24h: number };
  assets: { count: number; bytes: number };
  visits: { v_24h: number; v_7d: number; visitors_24h: number };
  online: { boards: number; peers: number };
};

type Tile = { label: string; value: string; hint?: string };

function Tiles({ title, tiles }: { title: string; tiles: Tile[] }) {
  return (
    <div className="wb-admin-summary__group">
      <h3 className="wb-admin-summary__group-title">{title}</h3>
      <div className="wb-admin-summary__tiles">
        {tiles.map((t) => (
          <div className="wb-admin-summary__tile" key={t.label}>
            <span className="wb-admin-summary__tile-value">{t.value}</span>
            <span className="wb-admin-summary__tile-label">{t.label}</span>
            {t.hint ? <span className="wb-admin-summary__tile-hint">{t.hint}</span> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminSummaryTab({ kind }: { kind: "school" | "external" }) {
  // При возврате «Назад» сводка сразу на месте и тихо обновляется.
  const [data, setData] = useAdminViewState<AdminSummary | null>(`admin:${kind}:summary`, null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(data === null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<AdminSummary>(`/api/v1/admin/summary?kind=${kind}`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Не удалось загрузить сводку");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kind]);

  if (loading) return <p className="wb-cabinet-plan-hint">Загрузка сводки…</p>;
  if (err) {
    return (
      <p className="wb-cabinet-toast wb-cabinet-toast--err" role="alert">
        {err}
      </p>
    );
  }
  if (!data) return null;

  return (
    <div className="wb-admin-summary">
      <Tiles
        title={kind === "school" ? "Школьные пользователи" : "Внешние пользователи"}
        tiles={[
          { label: "Всего", value: formatNumber(data.users.total) },
          { label: "Новых за 7 дней", value: formatNumber(data.users.new_7d) },
        ]}
      />
      <Tiles
        title={kind === "school" ? "Доски школьных аккаунтов" : "Доски внешних аккаунтов"}
        tiles={[
          { label: "Активных", value: formatNumber(data.boards.active) },
          { label: "В корзине", value: formatNumber(data.boards.trashed) },
          { label: "Правили за сутки", value: formatNumber(data.boards.touched_24h) },
          { label: "Правили за неделю", value: formatNumber(data.boards.touched_7d) },
        ]}
      />
      <Tiles
        title="Гостевые доски (общие для обеих админок)"
        tiles={[
          {
            label: "Активных",
            value: formatNumber(data.guest.boards),
            hint: "созданы без аккаунта",
          },
          { label: "Визитов за сутки", value: formatNumber(data.guest.v_24h) },
        ]}
      />
      <Tiles
        title={kind === "school" ? "Активность школьных досок" : "Активность внешних досок"}
        tiles={[
          { label: "Визитов за сутки", value: formatNumber(data.visits.v_24h) },
          {
            label: "Посетителей за сутки",
            value: formatNumber(data.visits.visitors_24h),
            hint: "аккаунт или IP",
          },
          { label: "Визитов за неделю", value: formatNumber(data.visits.v_7d) },
          {
            label: "Сейчас на досках",
            value: formatNumber(data.online.peers),
            hint: `досок: ${data.online.boards}`,
          },
        ]}
      />
      <Tiles
        title={kind === "school" ? "Файлы школьных аккаунтов" : "Файлы внешних аккаунтов"}
        tiles={[
          { label: "Файлов", value: formatNumber(data.assets.count) },
          { label: "Занято", value: formatBytes(data.assets.bytes) },
        ]}
      />
    </div>
  );
}
